import { useState, type FormEvent } from 'react';
import { EVENT_COLORS, type CategoryDef } from '@td/shared';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useRestoreCategory,
} from '../hooks/useCategories';
import { ApiRequestError } from '../lib/api';
import { useConfirm } from './ConfirmDialog';
import { Modal } from './Modal';

function ColorRow({ value, onPick }: { value: string; onPick: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {EVENT_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          aria-label={`Use colour ${c}`}
          aria-pressed={value === c}
          className={[
            'h-6 w-6 rounded-md border-2 transition-transform',
            value === c ? 'scale-110 border-espresso' : 'border-transparent hover:scale-105',
          ].join(' ')}
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  );
}

function Row({
  cat,
  canRemove,
  onRemoved,
}: {
  cat: CategoryDef;
  canRemove: boolean;
  onRemoved: (msg: string) => void;
}) {
  const update = useUpdateCategory();
  const remove = useDeleteCategory();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(cat.label);

  async function onRemove() {
    const ok = await confirm({
      title: `Remove "${cat.label}"?`,
      body: cat.builtin
        ? 'Any events using it move to another category. You can bring it back later.'
        : 'Any events using it move to another category.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;

    try {
      const result = await remove.mutateAsync(cat.key);
      onRemoved(
        result.reassigned > 0
          ? `${result.reassigned} event${result.reassigned === 1 ? '' : 's'} moved to ${result.movedTo}.`
          : `"${cat.label}" removed.`,
      );
    } catch (err) {
      onRemoved(err instanceof ApiRequestError ? err.message : 'Could not remove that.');
    }
  }

  return (
    <li className="rounded-lg border border-sand/70 px-3 py-2">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className="h-4 w-4 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
          style={{ backgroundColor: cat.color }}
          aria-label={`Change the colour of ${cat.label}`}
        />
        <input
          className="flex-1 bg-transparent text-sm text-espresso outline-none"
          value={label}
          maxLength={40}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => {
            const t = label.trim();
            if (t && t !== cat.label) update.mutate({ key: cat.key, label: t });
            else setLabel(cat.label);
          }}
          aria-label={`Rename ${cat.label}`}
        />
        <button
          onClick={() => void onRemove()}
          disabled={!canRemove}
          title={canRemove ? `Remove ${cat.label}` : 'Keep at least one category'}
          aria-label={`Remove ${cat.label}`}
          className="text-stone hover:text-rust disabled:cursor-not-allowed disabled:opacity-30"
        >
          ✕
        </button>
      </div>

      {open && (
        <div className="mt-2 pl-6">
          <ColorRow
            value={cat.color}
            onPick={(color) => {
              update.mutate({ key: cat.key, color });
              setOpen(false);
            }}
          />
        </div>
      )}
    </li>
  );
}

export function CategoryManager({ onClose }: { onClose: () => void }) {
  const { categories, hidden } = useCategories();
  const create = useCreateCategory();
  const restore = useRestoreCategory();

  const [label, setLabel] = useState('');
  const [color, setColor] = useState<string>(EVENT_COLORS[0]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = label.trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({ label: trimmed, color });
      setLabel('');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not add that category.');
    }
  }

  return (
    <Modal
      onClose={onClose}
      label="Categories"
      className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card bg-white p-5 shadow-lifted"
    >
      <h2 className="mb-1 text-lg">Categories</h2>
      <p className="mb-4 text-xs text-stone">
        Click a colour dot to change it, or the name to rename. Removing one moves its events to
        another category.
      </p>

      <ul className="space-y-1.5">
        {categories.map((c) => (
          <Row
            key={c.key}
            cat={c}
            canRemove={categories.length > 1}
            onRemoved={(msg) => {
              setNotice(msg);
              setError(null);
            }}
          />
        ))}
      </ul>

      {notice && <p className="mt-2 text-xs text-olive">{notice}</p>}

      {hidden.length > 0 && (
        <div className="mt-4 rounded-lg border border-dashed border-sand p-3">
          <p className="mb-2 text-xs text-stone">Removed built-ins</p>
          <div className="flex flex-wrap gap-1.5">
            {hidden.map((c) => (
              <button
                key={c.key}
                onClick={() => restore.mutate(c.key)}
                className="flex items-center gap-1.5 rounded-lg border border-sand px-2.5 py-1 text-xs text-walnut hover:bg-linen"
                title={`Bring ${c.label} back`}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full opacity-50"
                  style={{ backgroundColor: c.color }}
                />
                {c.label}
                <span className="text-stone">+</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={onAdd} className="mt-4 rounded-lg bg-linen/60 p-3">
        <label className="label" htmlFor="cat-name">
          New category
        </label>
        <div className="flex gap-2">
          <input
            id="cat-name"
            className="field"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Field trip"
            maxLength={40}
          />
          <button type="submit" className="btn-primary shrink-0" disabled={!label.trim()}>
            Add
          </button>
        </div>
        <div className="mt-2">
          <ColorRow value={color} onPick={setColor} />
        </div>
        {error && (
          <p role="alert" className="mt-2 text-sm text-rust">
            {error}
          </p>
        )}
      </form>

      <div className="mt-4 flex justify-end">
        <button onClick={onClose} className="btn-secondary">
          Done
        </button>
      </div>
    </Modal>
  );
}
